import { useState, useEffect } from 'react';
import { CreditCard, Wrench, FileText, AlertCircle, Loader2, KeyRound, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../../context/useAppContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { supabase } from '../../lib/supabase';

export default function TenantDashboard() {
    const { user } = useAppContext();
    const [loading, setLoading] = useState(true);
    const [tenant, setTenant] = useState<any>(null);
    const [property, setProperty] = useState<any>(null);
    const [payments, setPayments] = useState<any[]>([]);
    const [tickets, setTickets] = useState<any[]>([]);

    useEffect(() => {
        const fetchTenantData = async () => {
            if (!user.id && !user.email) return;
            try {
                setLoading(true);

                // 1. Find the tenant_link for this user
                let tenantLink: any = null;

                if (user.id) {
                    // Primary: by user ID — no status filter, any status means they're linked
                    const { data } = await supabase
                        .from('tenant_links')
                        .select('*')
                        .eq('tenant_user_id', user.id)
                        .order('created_at', { ascending: false })
                        .limit(1)
                        .maybeSingle();
                    tenantLink = data;
                }

                // Fallback: email match (tenant_user_id not yet written — pending invite)
                if (!tenantLink && user.email) {
                    const { data } = await supabase
                        .from('tenant_links')
                        .select('*')
                        .eq('tenant_email', user.email)
                        .order('created_at', { ascending: false })
                        .limit(1)
                        .maybeSingle();
                    tenantLink = data;
                }

                if (!tenantLink) {
                    setLoading(false);
                    return;
                }
                setTenant(tenantLink);

                if (tenantLink.property_id) {
                    const { data: propData } = await supabase
                        .from('properties')
                        .select('*')
                        .eq('id', tenantLink.property_id)
                        .single();
                    setProperty(propData);

                    const { data: ticketData } = await supabase
                        .from('maintenance_tickets')
                        .select('*')
                        .eq('property_id', tenantLink.property_id)
                        .order('created_at', { ascending: false });
                    setTickets(ticketData || []);

                    // Payments — silent fail if table missing
                    const { data: payData, error: payError } = await supabase
                        .from('rent_payments')
                        .select('*')
                        .or(`tenant_link_id.eq.${tenantLink.id},property_id.eq.${tenantLink.property_id}`)
                        .order('due_date', { ascending: false });
                    if (!payError) setPayments(payData || []);
                }
            } catch (err) {
                console.error('[TenantDashboard] fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchTenantData();
    }, [user.id, user.email]);

    // ── Loading ──────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <Loader2 className="animate-spin" size={36} color="#6366f1" />
            </div>
        );
    }

    // ── Not linked yet ───────────────────────────────────────────────────────

    if (!tenant) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '65vh' }}>
                <div style={{ textAlign: 'center', maxWidth: 420, padding: '2rem' }}>
                    <div style={{
                        width: 72, height: 72, borderRadius: '20px',
                        background: 'rgba(99, 102, 241, 0.08)',
                        border: '1.5px solid rgba(99, 102, 241, 0.18)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 1.75rem',
                    }}>
                        <KeyRound size={32} color="#6366f1" />
                    </div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0 0 0.75rem', color: 'var(--color-text)' }}>
                        Connect your home
                    </h2>
                    <p style={{ color: 'var(--color-text-secondary)', margin: '0 0 2rem', lineHeight: 1.65 }}>
                        Your account isn't linked to a property yet. Enter the invite code your landlord gave you to get started.
                    </p>
                    <Link
                        to="/app/tenant/connect"
                        className="btn btn-primary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0.875rem 2rem', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}
                    >
                        <KeyRound size={18} />
                        Enter Invite Code
                    </Link>
                    <p style={{ marginTop: '1.25rem', fontSize: '0.8125rem', color: 'var(--color-text-tertiary)' }}>
                        Don't have a code? Ask your landlord to invite you from their dashboard.
                    </p>
                </div>
            </div>
        );
    }

    // ── Dashboard ────────────────────────────────────────────────────────────

    const nextPayment = payments.find(p => p.status === 'pending' || p.status === 'overdue');
    const overduePayment = payments.find(p => p.status === 'overdue');
    const openTickets = tickets.filter(t => t.status !== 'resolved').length;

    return (
        <div className="page">

            {/* Page header */}
            <div className="page__header" style={{ marginBottom: '1.75rem' }}>
                <div>
                    <h1 className="page__title">
                        Welcome home, {user.name?.split(' ')[0] || 'Tenant'}
                    </h1>
                    {property && (
                        <p className="page__subtitle" style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '0.375rem' }}>
                            <Home size={13} style={{ flexShrink: 0 }} />
                            {property.name}
                            {property.address && ` · ${property.address}`}
                        </p>
                    )}
                </div>
            </div>

            {/* Overdue alert */}
            {overduePayment && (
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem 1.25rem',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 'var(--radius-lg)',
                    display: 'flex', alignItems: 'center', gap: '0.875rem',
                }}>
                    <AlertCircle size={20} color="#dc2626" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.9375rem' }}>Payment Overdue</div>
                        <div style={{ fontSize: '0.875rem', color: '#b91c1c', marginTop: '2px' }}>
                            Your rent of {formatCurrency(overduePayment.amount)} was due {formatDate(overduePayment.due_date)}.
                        </div>
                    </div>
                    <Link
                        to="/app/tenant/payments"
                        style={{ background: '#dc2626', color: '#fff', borderRadius: '8px', textDecoration: 'none', padding: '0.4rem 1rem', fontWeight: 700, fontSize: '0.8125rem', flexShrink: 0 }}
                    >
                        View
                    </Link>
                </div>
            )}

            {/* KPI Cards — .overview-grid / .overview-card defined in TenantLayout.css */}
            <div className="overview-grid">

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">Next Rent Due</span>
                        <div className="overview-card__icon" style={{ background: '#dbeafe' }}>
                            <CreditCard size={18} color="#2563eb" />
                        </div>
                    </div>
                    {nextPayment ? (
                        <>
                            <div className="overview-card__value">{formatCurrency(nextPayment.amount)}</div>
                            <div className={`overview-card__trend ${nextPayment.status === 'overdue' ? 'trend-down' : 'trend-neutral'}`}>
                                Due {formatDate(nextPayment.due_date)}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="overview-card__value">{formatCurrency(property?.rent_amount || 0)}</div>
                            <div className="overview-card__trend trend-up">All caught up!</div>
                        </>
                    )}
                </div>

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">Open Maintenance</span>
                        <div className="overview-card__icon" style={{ background: '#fef3c7' }}>
                            <Wrench size={18} color="#d97706" />
                        </div>
                    </div>
                    <div className="overview-card__value">{openTickets}</div>
                    <div className="overview-card__trend trend-neutral">
                        <Link to="/app/tenant/maintenance" style={{ color: 'inherit', textDecoration: 'none' }}>
                            {openTickets === 0 ? 'No open issues' : `${openTickets} open ticket${openTickets > 1 ? 's' : ''}`} →
                        </Link>
                    </div>
                </div>

                {property?.rent_amount != null && (
                    <div className="overview-card">
                        <div className="overview-card__header">
                            <span className="overview-card__title">Monthly Rent</span>
                            <div className="overview-card__icon" style={{ background: '#ede9fe' }}>
                                <Home size={18} color="#7c3aed" />
                            </div>
                        </div>
                        <div className="overview-card__value">{formatCurrency(property.rent_amount)}</div>
                        <div className="overview-card__trend trend-neutral">per month</div>
                    </div>
                )}

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">Documents</span>
                        <div className="overview-card__icon" style={{ background: '#dcfce7' }}>
                            <FileText size={18} color="#16a34a" />
                        </div>
                    </div>
                    <div className="overview-card__value" style={{ fontSize: '1.1rem', fontWeight: 700, paddingTop: '0.125rem' }}>View All</div>
                    <div className="overview-card__trend trend-neutral">
                        <Link to="/app/tenant/documents" style={{ color: 'inherit', textDecoration: 'none' }}>
                            Lease & documents →
                        </Link>
                    </div>
                </div>
            </div>

            {/* Quick Actions — .quick-action-grid / .quick-action-card defined in TenantLayout.css */}
            <div className="data-panel">
                <div className="data-panel__header">
                    <span className="data-panel__title">Quick Actions</span>
                </div>
                <div className="quick-action-grid">
                    <Link to="/app/tenant/payments" className="quick-action-card">
                        <div className="quick-action-card__icon">
                            <CreditCard size={22} color="#2563eb" />
                        </div>
                        <span className="quick-action-card__label">Pay Rent</span>
                    </Link>

                    <Link to="/app/tenant/maintenance" className="quick-action-card">
                        <div className="quick-action-card__icon">
                            <Wrench size={22} color="#d97706" />
                        </div>
                        <span className="quick-action-card__label">Report Issue</span>
                    </Link>

                    <Link to="/app/tenant/documents" className="quick-action-card">
                        <div className="quick-action-card__icon">
                            <FileText size={22} color="#6366f1" />
                        </div>
                        <span className="quick-action-card__label">Documents</span>
                    </Link>

                    <Link to="/app/tenant/settings" className="quick-action-card">
                        <div className="quick-action-card__icon">
                            <Home size={22} color="#16a34a" />
                        </div>
                        <span className="quick-action-card__label">My Home</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}
